import React, { useState } from 'react';
import { FaShieldAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ChildSafetySection: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowScrollIndicator(target.scrollTop > 20);
  };

  const sections = [
    {
      number: 1,
      icon: FaShieldAlt,
      title: 'Published Standards Against CSAE',
      content: `collectedAF explicitly prohibits all forms of child sexual abuse and exploitation (CSAE) on our platform. We strictly prohibit users from creating, uploading, or distributing content that facilitates the exploitation or abuse of children. This includes all child sexual abuse materials (CSAM). Any violation of this policy will result in immediate account termination and reporting to the relevant authorities.

We prohibit the use of our app to endanger children. This includes but is not limited to:
• Inappropriate interaction targeted at a child (for example, groping or caressing)
• Child grooming (for example, befriending a child online to facilitate, either online or offline, sexual contact and/or exchanging sexual imagery with that child)
• Sexualization of a minor (for example, imagery that depicts, encourages, or promotes the sexual abuse of children or the portrayal of children in a manner that could result in the sexual exploitation of children)
• Sextortion (for example, threatening or blackmailing a child by using real or alleged access to a child's intimate images)
• Trafficking of a child (for example, advertising or solicitation of a child for commercial sexual exploitation)

We also do not allow content that appeals to children but contains adult themes, including apps with excessive violence, blood, and gore; apps that depict or encourage harmful and dangerous activities; and apps that promote negative body or self image.`
    },
    {
      number: 2,
      icon: FaShieldAlt,
      title: 'Age-Segmented Experience for Minors',
      content: `collectedAF enforces strict age-based separation within the app. During registration, every user's age is verified and used to determine their experience tier:

• Minors (under 18) are placed in a controlled environment where they can only discover, interact with, and communicate with other verified minors. Adult users are completely invisible to minors and vice versa.
• Adults (18+) operate in the standard experience and cannot view, contact, or interact with minor accounts in any way.

This architectural separation is enforced at the platform level and cannot be bypassed by individual users. Key safeguards include:
• Proximity discovery, collections, and all social features are filtered by age group
• Messaging and communication channels are restricted to same-age-group peers only
• Content visibility, profiles, and user cards are segmented so minors and adults never appear in each other's feeds
• Age misrepresentation is a terminable offense and is actively monitored`
    },
    {
      number: 3,
      icon: FaShieldAlt,
      title: 'In-App Reporting Mechanism',
      content: `collectedAF provides an in-app mechanism for users to report concerns, inappropriate content, or behavior without leaving the app. Users can report other users directly from their profile, report content through contextual menus, or contact our support team within the app. All reports are reviewed promptly by our moderation team.

Users may also report concerns via email at support@collectedAF.com.`
    },
    {
      number: 4,
      icon: FaShieldAlt,
      title: 'Addressing CSAM',
      content: `We take appropriate action to address child sexual abuse material (CSAM), including removing such content and reporting it to the authorities upon obtaining actual knowledge of it. We actively monitor and enforce policies to detect and prevent such content through moderation tools, reporting mechanisms, and collaboration with law enforcement and organizations specializing in child protection.

When CSAM is identified, we will:
• Immediately remove the content from our platform
• Terminate the offending user's account
• Report to the National Center for Missing & Exploited Children (NCMEC) and/or relevant regional authorities
• Preserve relevant evidence as required by law
• Cooperate fully with law enforcement investigations`
    },
    {
      number: 5,
      icon: FaShieldAlt,
      title: 'Compliance with Child Safety Laws',
      content: `collectedAF complies with applicable child safety laws and regulations, including:

• Maintaining a process to report confirmed CSAM to NCMEC or relevant regional authorities
• Complying with mandatory reporting obligations under federal and state law
• Not knowingly collecting personally identifiable information from children under 13 years of age
• Immediately deleting data from our servers upon discovering that a child under 13 has provided personal information`
    },
    {
      number: 6,
      icon: FaShieldAlt,
      title: 'Child Safety Point of Contact',
      content: `We maintain a designated child safety point of contact who is ready and able to coordinate with Google Play and law enforcement on CSAE-related matters, including enforcement and review procedures.

Child Safety Contact:
Email: support@collectedAF.com
Organization: collectedAF LLC`
    },
    {
      number: 7,
      icon: FaShieldAlt,
      title: 'How to Report Child Safety Concerns',
      content: `If you encounter any content or activity that appears to involve child endangerment, abuse, exploitation, or CSAE, please report it immediately:

• In-App: Use the report feature available on user profiles and content
• Email: support@collectedAF.com

Your report will be treated with the utmost urgency and confidentiality. If you believe a child is in immediate danger or has been subject to abuse, exploitation, or trafficking, please contact your local law enforcement immediately.

We reserve the right to take immediate action against accounts or individuals found to be engaging in prohibited activities, including but not limited to reporting to appropriate authorities, terminating access to our Service, and cooperating fully with law enforcement investigations.`
    },
  ];

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 45%, #fbbf24 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>
        
        <div className="relative h-full flex items-center justify-center">
          <div className="colorful-ring bg-BLACK/92 backdrop-blur-xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:h-full transition-colors shadow-2xl flex flex-col">
            <h2 className="text-4xl lg:text-5xl font-bold text-WHITE mb-3 text-center">Child Safety Standards</h2>
            <p
              className="text-lg font-bold mb-6 text-center"
              style={{
                background: 'linear-gradient(90deg, #ffffff 0%, #0066cc 50%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Published Standards Against Child Sexual Abuse and Exploitation
            </p>
            
            <div className={`absolute top-32 left-0 right-0 h-12 bg-gradient-to-b from-ORANGE/30 to-transparent pointer-events-none transition-opacity duration-300 z-10 ${showScrollIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-center pt-2">
                <FaChevronUp className="text-ORANGE animate-bounce" size={24} />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-4 pr-2" onScroll={handleScroll}>
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={index} className="bg-WHITE/5 rounded-xl border border-WHITE/10 hover:border-ORANGE/50 transition-colors">
                    <button
                      onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-WHITE/10 flex items-center justify-center border-2 border-WHITE/25">
                        <Icon className="text-ORANGE" size={20} />
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-ORANGE flex items-center justify-center flex-shrink-0">
                          <span className="text-WHITE text-lg font-bold">{section.number}</span>
                        </div>
                        <h4 className="text-WHITE text-lg font-bold">{section.title}</h4>
                      </div>
                      {openAccordion === index ? (
                        <FaChevronUp className="text-ORANGE" size={20} />
                      ) : (
                        <FaChevronDown className="text-WHITE/50" size={20} />
                      )}
                    </button>
                    
                    {openAccordion === index && (
                      <div className="px-4 pb-4 pt-0">
                        <p className="text-WHITE/70 text-sm leading-relaxed whitespace-pre-line">
                          {section.content}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildSafetySection;
